import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import Investor from '../../../models/Investor';
import { fmtCurrency, fmtPct } from './contactPortfolioFormatUtils';
import type { FundBreakdownEntry } from './contactPortfolioTypes';

export default function ContactPortfolioFundCard({
  investor,
  breakdown,
  navigate,
  closed = false,
}: {
  investor: Investor;
  breakdown?: FundBreakdownEntry;
  navigate: (path: string) => void;
  closed?: boolean;
}) {
  const fund = investor.fundOpportunity as
    | { id?: string; name?: string; status?: string }
    | undefined;
  const fundName = fund?.name ?? breakdown?.fundName ?? 'Unknown Fund';
  const fundId = fund?.id ?? investor.fundOpportunityId;
  const committed =
    investor.committedInvestment ?? investor.preferredInvestment ?? breakdown?.investorInvestment;

  const handleClick = () => {
    if (fundId && investor.id) {
      navigate(`/opportunities/${fundId}/investors/${investor.id}?tab=fund-update`);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: 'background.paper',
        cursor: fundId ? 'pointer' : 'default',
        '&:hover': fundId ? { bgcolor: 'action.hover' } : {},
        transition: 'background-color 0.15s',
        opacity: closed ? 0.75 : 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: breakdown ? 1.5 : 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap title={fundName}>
            {fundName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {fmtCurrency(committed)} committed{closed ? ' · Closed' : ''}
          </Typography>
        </Box>
        <Chip
          label={closed ? 'Closed' : 'Invested'}
          color={closed ? 'default' : 'success'}
          size="small"
          sx={{ ml: 1, flexShrink: 0 }}
        />
      </Box>

      {breakdown && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { label: 'IRR', value: fmtPct(breakdown.irr) },
            { label: 'ROE', value: fmtPct(breakdown.roe) },
            { label: 'Deployed', value: fmtCurrency(breakdown.deployedAmount) },
            { label: 'Forecast Return', value: fmtCurrency(breakdown.forecastReturn) },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                flexBasis: '22%',
                flexGrow: 1,
                minWidth: 72,
                bgcolor: 'action.hover',
                borderRadius: 1.5,
                py: 0.75,
                px: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  fontSize: '0.65rem',
                }}
              >
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {breakdown?.latestMonth && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
          Data as at {breakdown.latestMonth}
        </Typography>
      )}
    </Box>
  );
}
